import { Card } from "@/components/ui/card";
import { OpportunityCard } from "@/opportunity/OpportunityCard";
import { installationHealthStatusBackgroundClass } from "@/installation/installation";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BellOff, ChevronDown } from "lucide-react";
import React, { Suspense } from "react";
import { useOpportunities } from "@/opportunity/api";
import { Opportunity, OpportunityTableFilters } from "@/opportunity/opportunity";
import { Spinner } from "@/components/ui/spinner";

const isSnoozed = (o: Opportunity, snoozedLocationIds?: Set<number>, snoozedEquipmentIds?: Set<number>) =>
  !!(snoozedLocationIds?.has(o.location.id) || snoozedEquipmentIds?.has(o.installation.equipment));

const OpportunitiesList = ({ opportunities, snoozedLocationIds, snoozedEquipmentIds }: {
  opportunities: Opportunity[],
  snoozedLocationIds?: Set<number>,
  snoozedEquipmentIds?: Set<number>,
}) => {
  const hasSnoozeSets = (snoozedLocationIds && snoozedLocationIds.size > 0) || (snoozedEquipmentIds && snoozedEquipmentIds.size > 0);

  if (!hasSnoozeSets) {
    return (
      <div className="flex flex-col gap-4">
        {opportunities.map((opportunity) => (
          <OpportunityCard key={opportunity.id} opp={opportunity}/>
        ))}
      </div>
    );
  }

  const nonSnoozed = opportunities.filter(o => !isSnoozed(o, snoozedLocationIds, snoozedEquipmentIds));
  const snoozed = opportunities.filter(o => isSnoozed(o, snoozedLocationIds, snoozedEquipmentIds));

  return (
    <div className="flex flex-col gap-4">
      {nonSnoozed.map((opportunity) => (
        <OpportunityCard key={opportunity.id} opp={opportunity}/>
      ))}
      {snoozed.length > 0 && (
        <SnoozedIssues opportunities={snoozed} />
      )}
    </div>
  );
};

const SnoozedIssues = ({ opportunities }: { opportunities: Opportunity[] }) => {
  if (opportunities.length === 0) return null;

  return (
    <Collapsible defaultOpen={false} className="group/snoozed flex flex-col gap-2">
      <div className="flex flex-row gap-2 items-center">
        <BellOff size={14} className="text-snooze-purple" />
        <div className="text-snooze-purple text-sm">Snoozed ({opportunities.length})</div>
        <CollapsibleTrigger>
          <div className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md">
            <ChevronDown
              size={14}
              className="ml-auto transition-transform duration-200 group-data-[state=open]/snoozed:rotate-180"
            />
          </div>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="opacity-50 flex flex-col gap-4">
          {opportunities.map((opportunity) => (
            <OpportunityCard key={opportunity.id} opp={opportunity}/>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const NoIssuesMessage = ({ numDataPointsPerMinute }: { numDataPointsPerMinute: number }) => (
  <div className="text-gray-600 bg-gray-50 rounded-lg p-4">
    <div className="mb-4 flex flex-row gap-2 items-center">
      <div className={`h-3 w-3 rounded-full ${installationHealthStatusBackgroundClass["green"]}`}/>
      <p>No active issues.</p>
    </div>
    <p className="mb-4">
      Thalo HVAC Copilot reviews{' '}
      <span
        className="font-bold bg-gradient-to-r bg-clip-text text-transparent from-thalo-blue via-thalo-blueLight to-thalo-blue animate-text">
        {numDataPointsPerMinute.toLocaleString()} data points per minute
      </span>{' '}
      from your equipment.
    </p>
    <p>When it detects an issue, it shows up here.</p>
  </div>
);

const ResolvedIssues = ({ opportunities }: { opportunities: Opportunity[] }) => {
  if (opportunities.length === 0) return null;

  return (
    <Collapsible defaultOpen={false} className="group/collapsible flex flex-col gap-2">
      <div className="flex flex-row gap-2 items-center">
        <div className="text-gray-500">Resolved issues</div>
        <CollapsibleTrigger>
          <div className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md">
            <ChevronDown
              size={14}
              className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180"
            />
          </div>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="max-h-[400px] overflow-y-auto">
          <OpportunitiesList opportunities={opportunities}/>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ActiveIssues = ({ opportunities, numDataPointsPerMinute, snoozedLocationIds, snoozedEquipmentIds }: {
  opportunities: Opportunity[],
  numDataPointsPerMinute: number,
  snoozedLocationIds?: Set<number>,
  snoozedEquipmentIds?: Set<number>,
}) => (
  opportunities.length > 0
    ? (
      <div className="max-h-[600px] overflow-y-auto">
        <OpportunitiesList opportunities={opportunities} snoozedLocationIds={snoozedLocationIds} snoozedEquipmentIds={snoozedEquipmentIds}/>
      </div>
    )
    : <NoIssuesMessage numDataPointsPerMinute={numDataPointsPerMinute}/>
);

const IssuesContent = ({ filters, numDataPointsPerMinute, snoozedLocationIds, snoozedEquipmentIds }: {
  filters: OpportunityTableFilters,
  numDataPointsPerMinute: number,
  snoozedLocationIds?: Set<number>,
  snoozedEquipmentIds?: Set<number>,
}) => {
  const { data: { results: opportunitiesRequiringAction } } = useOpportunities({
    filters: {
      ...filters,
      requires_action: true
    },
    sorting: [{ id: "last_detected_date", desc: true }],
    pageSize: 8,
  });

  const { data: { results: opportunitiesNotRequiringAction } } = useOpportunities({
    filters: {
      ...filters,
      requires_action: false
    },
    sorting: [{ id: "last_detected_date", desc: true }],
    pageSize: 5,
  });

  return (
    <>
      <ActiveIssues
        opportunities={opportunitiesRequiringAction}
        numDataPointsPerMinute={numDataPointsPerMinute}
        snoozedLocationIds={snoozedLocationIds}
        snoozedEquipmentIds={snoozedEquipmentIds}
      />
      <ResolvedIssues opportunities={opportunitiesNotRequiringAction}/>
    </>
  );
};

export const IssuesCard = ({ filters, numDataPointsPerMinute, hideTitle = false, snoozedLocationIds, snoozedEquipmentIds }: {
  filters: OpportunityTableFilters,
  numDataPointsPerMinute: number,
  hideTitle?: boolean,
  snoozedLocationIds?: Set<number>,
  snoozedEquipmentIds?: Set<number>,
}) => {
  const content = (
    <div className="flex flex-col gap-2 lg:gap-4">
      {!hideTitle && <h2 className="font-semibold text-lg">Issue Activity</h2>}
      <Suspense fallback={<Spinner/>}>
        <IssuesContent
          filters={filters}
          numDataPointsPerMinute={numDataPointsPerMinute}
          snoozedLocationIds={snoozedLocationIds}
          snoozedEquipmentIds={snoozedEquipmentIds}
        />
      </Suspense>
    </div>
  );

  if (hideTitle) {
    return content;
  }

  return (
    <Card className="p-3 lg:p-4 flex flex-col gap-2 lg:gap-4">
      {content}
    </Card>
  );
};